import React, { useCallback, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as Actions from '../redux/actions';
import '../styles/orders.scss';
import OrderWizard from './OrderWizard';
import OrderDetailsModal from './OrderDetailsModal';
import { formatOrderWhen, orderRecipientName } from './orderUtils';

const orderTitle = (order) => {
  const name = orderRecipientName(order);
  if (name && name !== 'Order') return name;
  return `Order #${order.id}`;
};

const OrdersMain = (props) => {
  const [listTab, setListTab] = useState('drafts');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [wizardKey, setWizardKey] = useState(0);
  const { showNav, initializeOrders, authToken, loadOrderDraft, deleteOrderDraft, clearActiveOrderDraft } = props;

  useEffect(() => {
    showNav(true);
  }, [showNav]);

  useEffect(() => {
    if (authToken) {
      initializeOrders(authToken);
    }
  }, [authToken, initializeOrders]);

  const drafts = props.orderDrafts || [];
  const submitted = props.orderHistory || [];
  const isSubmittedTab = listTab === 'submitted';
  const list = isSubmittedTab ? submitted : drafts;
  const activeId = props.activeOrderDraft?.id;

  const switchListTab = (tab) => {
    if (tab === listTab) return;
    setListTab(tab);
    setSelectedOrder(null);
  };

  const handleOpenDraft = (id) => {
    loadOrderDraft(authToken, id, () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleDeleteDraft = (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this order draft?')) return;
    const wasActive = activeId === id;
    deleteOrderDraft(authToken, id, () => {
      if (wasActive) {
        clearActiveOrderDraft();
        setWizardKey((k) => k + 1);
      }
    });
  };

  const closeSelectedOrder = useCallback(() => setSelectedOrder(null), []);

  return (
    <div className="orders-dashboard">
      <OrderWizard key={wizardKey} />

      <section className="order-history-panel" aria-label="Order drafts and submitted orders">
        <div className="order-history-toolbar">
          <div className="order-drafts-tabs" role="tablist" aria-label="Order list">
            <button
              type="button"
              role="tab"
              aria-selected={!isSubmittedTab}
              className={`order-drafts-tab${!isSubmittedTab ? ' order-drafts-tab--active' : ''}`}
              onClick={() => switchListTab('drafts')}
            >
              Drafts
              {drafts.length > 0 ? (
                <span className="order-drafts-tab-count">{drafts.length}</span>
              ) : null}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isSubmittedTab}
              className={`order-drafts-tab${isSubmittedTab ? ' order-drafts-tab--active' : ''}`}
              onClick={() => switchListTab('submitted')}
            >
              Submitted
              {submitted.length > 0 ? (
                <span className="order-drafts-tab-count">{submitted.length}</span>
              ) : null}
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <p className="order-history-empty">
            {isSubmittedTab
              ? 'No submitted orders yet.'
              : 'No drafts yet — build an order above and save it as a draft.'}
          </p>
        ) : (
          <ul className="order-history-list browser-default">
            {list.map((order) => {
              const when = formatOrderWhen(order.updated_at || order.created_at);
              const active = !isSubmittedTab && activeId === order.id;
              const lines = order.line_items || [];

              return (
                <li key={order.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`order-draft-row${active ? ' active' : ''}`}
                    onClick={() => {
                      if (isSubmittedTab) setSelectedOrder(order);
                      else handleOpenDraft(order.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (isSubmittedTab) setSelectedOrder(order);
                        else handleOpenDraft(order.id);
                      }
                    }}
                  >
                    <div className="order-draft-row-main">
                      <span className="order-draft-title">{orderTitle(order)}</span>
                      <span className="order-draft-meta">
                        {lines.length
                          ? `${lines.length} line${lines.length === 1 ? '' : 's'}`
                          : 'Empty'}
                        {when ? ` · ${when}` : ''}
                      </span>
                    </div>
                    {!isSubmittedTab ? (
                      <button
                        type="button"
                        className="order-draft-delete"
                        title="Delete draft"
                        aria-label="Delete draft"
                        onClick={(e) => handleDeleteDraft(e, order.id)}
                      >
                        <DeleteOutlineIcon className="order-draft-delete-icon" />
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {selectedOrder ? (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={closeSelectedOrder}
        />
      ) : null}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    orderHistory: state.orderHistory,
    orderDrafts: state.orderDrafts,
    activeOrderDraft: state.activeOrderDraft,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(OrdersMain);
